import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

@Component({
  selector: 'ideal-keywords-inline-view',
  imports: [],
  templateUrl: './keywords-inline-view.component.html',
  styleUrl: './keywords-inline-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeywordsInlineViewComponent {
  public readonly keywords = input<string[]>([]);
  public readonly match = input<string>('');
  public readonly matchType = input<'startsWith' | 'endsWith' | 'contains'>('startsWith');
  public readonly caseSensitiveMatch = input<boolean>(false);

  /**
   * HTML string with each keyword wrapped in `<span class="keyword">`, the matched
   * portion of the keyword wrapped in `<span class="match">`, and keywords separated
   * by `<span class="comma">, </span>`. Matched keywords are listed first; the original
   * order is kept within the matched and unmatched groups.
   */
  public readonly keywordsHtml = computed(() => {
    const match = this.match();
    const matchType = this.matchType();
    const caseSensitive = this.caseSensitiveMatch();

    return this.keywords()
      .map((keyword) => ({ keyword, start: this.matchIndex(keyword, match, matchType, caseSensitive) }))
      .sort((a, b) => Number(b.start >= 0) - Number(a.start >= 0))
      .map(({ keyword, start }) => `<span class="keyword">${this.highlight(keyword, match, start).toLowerCase()}</span>`)
      .join('<span class="comma">, </span>');
  });

  private highlight(keyword: string, match: string, start: number): string {
    if (start < 0) {
      return keyword;
    }
    const end = start + match.length;
    return `${keyword.slice(0, start)}<span class="match">${keyword.slice(start, end)}</span>${keyword.slice(end)}`;
  }

  /** Index of the matched part within `keyword`, or -1 when nothing matches. */
  private matchIndex(keyword: string, match: string, matchType: 'startsWith' | 'endsWith' | 'contains', caseSensitive: boolean): number {
    if (!match || match.length > keyword.length) {
      return -1;
    }
    const haystack = caseSensitive ? keyword : keyword.toLowerCase();
    const needle = caseSensitive ? match : match.toLowerCase();

    switch (matchType) {
      case 'startsWith':
        return haystack.startsWith(needle) ? 0 : -1;
      case 'endsWith':
        return haystack.endsWith(needle) ? haystack.length - needle.length : -1;
      case 'contains':
        return haystack.indexOf(needle);
    }
  }
}
